import {
  ConsolidatedForecastCreateData,
  ConsolidatedForecastCreateFromExistingData,
  ConsolidatedForecastCreateFromExistingSubEntitiyForecastsData
} from '../../core/api/types/consolidated-forecasting';
import { convertDDMMYYYtoYYYYMMDD } from '../../utils/dates';
import { NewConsolidatedForecastDataSource } from '../dashboard/types';
import { getFixedForecastWindowPayload } from './get-fixed-forecast-window-payload';
import { ForecastKindOption } from './types';

export interface GetCreateConsolidatedForecastRequestDataParams {
  datasource: NewConsolidatedForecastDataSource;
  duration: string;
  from: string;
  kind: ForecastKindOption;
  name: string;
  sourceForecast: string;
  sourceSubEntityForecasts: Record<string, string>;
}

/**
 * Builds the shared consolidated forecast create request used by create and duplicate flows.
 *
 * @param params - Current consolidated forecast form values.
 * @param params.datasource - Selected consolidated forecast creation method.
 * @param params.duration - Selected fixed duration in years for a 3-way forecast.
 * @param params.from - Selected Start date formatted as `dd/MM/yyyy`.
 * @param params.kind - Budget or 3-way forecast kind.
 * @param params.name - Forecast name entered by the user.
 * @param params.sourceForecast - Source consolidated forecast ID when duplicating.
 * @param params.sourceSubEntityForecasts - Source forecast IDs selected for sub-entities.
 * @returns The consolidated forecast create payload.
 */
export const getCreateConsolidatedForecastRequestData = ({
  datasource,
  duration,
  from,
  kind,
  name,
  sourceForecast,
  sourceSubEntityForecasts
}: GetCreateConsolidatedForecastRequestDataParams): ConsolidatedForecastCreateData => {
  const forecastFromDate = convertDDMMYYYtoYYYYMMDD(from.trim());
  const scenarioWindowPayload =
    kind === 'scenario'
      ? getFixedForecastWindowPayload({ duration, windowFrom: forecastFromDate! })
      : {};
  const requestBody = {
    method: datasource,
    kind,
    name: name.trim(),
    forecast_from_date: forecastFromDate,
    ...(kind === 'budget'
      ? {
        window_from: forecastFromDate,
        window_spec: {
          kind: 'custom-years' as const,
          duration_years: 1 as const
        }
      }
      : {}),
    ...scenarioWindowPayload
  } as ConsolidatedForecastCreateData;

  if (datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING) {
    (requestBody as ConsolidatedForecastCreateFromExistingData).source_consolidated_forecast_id =
      sourceForecast.trim();
  }

  if (datasource === NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS) {
    (requestBody as ConsolidatedForecastCreateFromExistingSubEntitiyForecastsData).source_forecast_ids =
      Object.values(sourceSubEntityForecasts);
  }

  return requestBody;
};
